import { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@rnd-payplus/alloy-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { usePageShell } from '../contexts/PageShellContext';

const COLORS = ['#0078D4', '#00A4EF', '#50E6FF', '#FFB900', '#E81123'];

const AnalyticsPage = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [exportFormat, setExportFormat] = useState('csv');
  const [transactionData, setTransactionData] = useState<any[]>([]);
  const [errorData, setErrorData] = useState<any[]>([]);
  const [platformData, setPlatformData] = useState<any[]>([]);
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllData();
  }, [dateRange]);

  const loadAllData = async () => {
    setLoading(true);

    const days = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : dateRange === '90days' ? 90 : 1;

    try {
      const fetchJson = async (url: string) => {
        const r = await fetch(url);
        if (!r.ok) throw new Error(`${url} → ${r.status}`);
        return r.json();
      };
      const [transactionsRes, sourcesRes, statusRes] = await Promise.all([
        fetchJson(`/api/analytics/transactions?days=${days}`),
        fetchJson('/api/analytics/by-source'),
        fetchJson('/api/analytics/by-status'),
      ]);

      setTransactionData(transactionsRes.map((d: any) => ({
        date: d.date,
        transactions: parseInt(d.count),
      })));

      setPlatformData(sourcesRes.map((s: any) => ({
        name: s.name,
        value: parseInt(s.value),
      })));

      // Real error data needs an hourly aggregate endpoint that does not yet
      // exist; show empty until that lands rather than synthesising fake hours.
      void statusRes;
      setErrorData([]);
      setPerformanceData([]);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setTransactionData([]);
      setPlatformData([]);
      setErrorData([]);
      setPerformanceData([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load analytics');
    }

    setLoading(false);
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`/api/analytics/export?format=${exportFormat}&range=${dateRange}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-${dateRange}.${exportFormat}`;
        a.click();
      } else {
        toast.error(`Failed to export analytics (${response.status})`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export analytics');
    }
  };

  usePageShell({
    title: 'Analytics & Reporting',
    primaryAction: (
      <Button onClick={handleExport}>
        <Download className="mr-2 h-4 w-4" />
        Export
      </Button>
    ),
    secondaryActions: (
      <>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="min-w-[150px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24hours">Last 24 Hours</SelectItem>
            <SelectItem value="7days">Last 7 Days</SelectItem>
            <SelectItem value="30days">Last 30 Days</SelectItem>
            <SelectItem value="90days">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={exportFormat} onValueChange={setExportFormat}>
          <SelectTrigger className="min-w-[120px]">
            <SelectValue placeholder="Format" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="csv">CSV</SelectItem>
            <SelectItem value="excel">Excel</SelectItem>
            <SelectItem value="pdf">PDF</SelectItem>
          </SelectContent>
        </Select>
      </>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Analytics' },
    ],
  });

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <h2 className="text-base font-medium leading-6 mb-4">Transaction Volume (Last 7 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={transactionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="transactions" stroke="#0078D4" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h2 className="text-base font-medium leading-6 mb-4">Error Rate (Last 24 Hours)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={errorData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="errors" fill="#E81123" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h2 className="text-base font-medium leading-6 mb-4">Platform Usage Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={platformData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {platformData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h2 className="text-base font-medium leading-6 mb-4">Performance Metrics</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="latency"
                stroke="#0078D4"
                fill="#0078D4"
                fillOpacity={0.6}
                name="Latency (ms)"
              />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="throughput"
                stroke="#00A4EF"
                fill="#00A4EF"
                fillOpacity={0.6}
                name="Throughput (req/s)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
